import { IIndicator, IInsightDialog, MessageTemplating } from '@jumpstart/core';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';
import * as d3 from 'd3';
import { TreeLayout } from 'd3-hierarchy';
import React, { MutableRefObject, useState } from 'react';
import './DialogTreeVisual.css';
import { useD3 } from '../../../core/react/use-d3';
import { useStateRef } from '../../../core/react/use-state-ref';
import { isCompletion, IValidationData, splitWords, testDog, testProfile, validateDialogNode } from './utils';

const margin = { top: 20, right: 120, bottom: 20, left: 120 };
const width = 960 - margin.right - margin.left;
const height = 500 - margin.top - margin.bottom;
const distanceMultiplier = 2; // Link distance multiplier
const padding: number = 8;

export interface EnhancedInsightDialog extends IInsightDialog {
  notes?: string | null;
  parent: string;
  answer: string | null;
}

export type InsightNode =
  d3.HierarchyNode<EnhancedInsightDialog>
  & { _children: any; children: any; x0: number; y0: number; x: number; y: number };

export interface IDialogTreeVisual {
  indicator: IIndicator;
  size: {
    width: number;
    height: number;
  };

  onRequestUpdate(nodes: InsightNode[]): void;

  onPasteInstructions(node: InsightNode, copy: InsightNode): void;

  onPasteSoft(node: InsightNode, copy: InsightNode): void;

  onPaste(node: InsightNode, copy: InsightNode): void;

  onOpen(node: InsightNode): void;

  onRemove(node: InsightNode): void;

  onAdd(node: InsightNode): void;

  onAssign(node: InsightNode, assignee: InsightNode): void;

  onEditAnswer(node: InsightNode): void;
}


const calculateLines = (d: InsightNode): string[] => {
  const response: string[] = !!d.parent ? splitWords(d.data.response ? `${isCompletion(d.data.completionCriteria) ? '⭐' : '💬'} Response: ${MessageTemplating.process(
    d.data.response,
    testDog,
    testProfile
  )}` : '', 15) : [];
  // const response: string[] = isCompletion(d.data.completionCriteria) ? splitWords(d.data.response ? `⭐ Response: ${MessageTemplating.process(
  //   d.data.response,
  //   testDog,
  //   testProfile
  // )}` : '⁉️No Response', 15) : [];
  if (response.length > 0) {
    response.push('\n');
  }
  const action: string[] = !isCompletion(d.data.completionCriteria) && d.data.isAction ? splitWords(d.data.body ? `✔️Action: ${MessageTemplating.process(
    d.data.body,
    testDog,
    testProfile
  )}` : '❌ No Action', 15) : [];

  if (action.length > 0) {
    action.push('\n');
  }
  const notes: string[] = d.data.notes ? splitWords(`📝 ${d.data.notes}`, 20) : [];
  if (notes.length > 0) {
    notes.push('\n');
  }
  const question: string[] = !isCompletion(d.data.completionCriteria) ? splitWords(d.data.question ? `${d.data.questionInline ? '❔' : '❓'} Question: ${MessageTemplating.process(
    d.data.question,
    testDog,
    testProfile
  )}` : '❌ Question Missing', 15) : [];

  // Info
  const validation: IValidationData = validateDialogNode(d.data, d.data.answer, d.parent && d.parent.data ? d.parent.data as any : null);
  const infoLines: string[] = [];
  let infoLine: string = '';
  if (validation.numErrors > 0 || validation.numWarnings > 0) {
    infoLines.push('\n');
    if (validation.numErrors > 0) {
      infoLine += `${validation.numErrors} ❗`;
    }
    if (validation.numWarnings > 0) {
      infoLine += `${validation.numErrors > 0 ? ', ' : ''}${validation.numWarnings} ⚠️`;
    }
  }
  if (d.data.reminders && d.data.reminders.length > 0) {
    infoLine += `, ${d.data.reminders.length} ⏱️`
  }
  if (d.data.highlights && d.data.highlights.length > 0) {
    infoLine += `, ${d.data.highlights.length} ☑️`
  }
  if (d.data.metaAttributes && Object.keys(d.data.metaAttributes).length > 0) {
    infoLine += `, ${Object.keys(d.data.metaAttributes).length} 🐕`
  }
  if (d.data.tips && d.data.tips.length > 0) {
    infoLine += `, ${d.data.tips.length} ❕`
  }
  if (d.data.widgets && d.data.widgets.length > 0) {
    infoLine += `, ${d.data.widgets.length} 💎`
  }
  if (d.data.details && d.data.details.length > 0) {
    infoLine += `, ${d.data.details.length} 📚`
  }
  infoLines.push(`(${infoLine})`);
  return [
    `ID: ${d.data.id}`,
    '\n',
    ...response,
    ...action,
    ...question,
    ...notes,
    ...infoLines
  ];
};

interface Point {
  x: number;
  y: number;
}

interface DOMRectSafe extends Point {
  height: number;
  width: number;
}

const calculateRect = (d: InsightNode): DOMRectSafe => {
  const element: HTMLOrSVGElement | null = document.getElementById(d.id as string);
  if (element && (element as SVGElement).getBoundingClientRect) {
    const rec: DOMRect = (element as SVGElement).getBoundingClientRect();
    if (rec) {
      let width: number = rec.width + (padding * 2);
      let height: number = rec.height + (padding * 2);
      return {
        ...rec,
        width: width,
        height: height,
        // For some reason x is height and y is width??
        x: d.x + (rec.height / 2),
        y: d.y + (rec.width / 2)
      };
    } else {
      return {
        width: 300,
        height: 100,
        x: d.x,
        y: d.y
      };
    }
  } else {
    return {
      width: 300,
      height: 100,
      x: d.x,
      y: d.y
    };
  }
};

type CopyTypes = 'hard' | 'soft' | 'instructions';
export default function DialogTreeVisual(props: IDialogTreeVisual) {
  const { size, indicator, onOpen, onRemove, onAdd, onEditAnswer, onAssign, onPaste, onPasteSoft, onPasteInstructions } = props;
  const [anchor, setAnchor] = useState<null | { element: HTMLElement | null, node: InsightNode; isValidNode: boolean }>(null);
  const [assigningNode, setAssigningNode] = useState(false);
  const [copyNode, setCopyNode] = useState<{node: InsightNode; type: CopyTypes} | null>(null);
  const [nodes, setNodes, nodesRef] = useStateRef<d3.HierarchyPointNode<IInsightDialog>[]>([]);

  const isUnique = (node: InsightNode) => {
    return nodesRef.current.filter((n) => n.data.id === node.data.id).length === 1;
  }

  const handleClear = () => {
    setAnchor(null);
    setCopyNode(null);
  }

  const handleCopy = (type: CopyTypes) => {
    if (anchor) {
      setCopyNode({node: anchor.node, type});
      setAnchor(null);
    }
  }

  const handlePaste = () => {
    if (copyNode && anchor) {
      switch(copyNode.type) {
        case 'soft':
          onPasteSoft(anchor.node, copyNode.node);
          break;
        case 'hard':
          onPaste(anchor.node, copyNode.node);
          break;
        case 'instructions':
          onPasteInstructions(anchor.node, copyNode.node);
          break;
        default:
          console.error('No option', copyNode.type);
      }
      setAnchor(null);
      // setCopyNode(null);
    }
  }

  const handleRemove = () => {
    if (!anchor) {
      return;
    }
    onRemove(anchor.node);
    setAnchor(null);
  };

  const handleRequestUpdate = () => {
    if (anchor && anchor.node) {
      props.onRequestUpdate(nodes as any);
      setAnchor(null);
    }
  }

  const handleAdd = () => {
    if (!anchor) {
      return;
    }
    onAdd(anchor.node);
    setAnchor(null);
  };

  const ref: MutableRefObject<any> = useD3(
    (svg: any) => {
      let i = 0;
      const duration = 750;

      // Remove previous
      svg.select('g.view').remove();

      const g = svg.append('g')
        .attr('class', 'view')
        .attr('cursor', 'grab')
        .attr('transform', 'translate('
          + margin.left + ',' + margin.top + ')');

      const tree: TreeLayout<any> = d3.tree()
        .size([height, width]);

      const root: d3.HierarchyNode<EnhancedInsightDialog> & { x0?: number, y0?: number } = d3.hierarchy({
        ...indicator.dialog,
        parent: 'dialog',
        answer: null
      } as EnhancedInsightDialog, (d: EnhancedInsightDialog) => {
        if (d && d.responses) {
          return Object.keys(d.responses).reduce((accumulator: EnhancedInsightDialog[], key: string) => {
            if (d.responses) {
              accumulator.push({ ...d.responses[key], parent: `${d.parent}.responses.${key}`, answer: key });
            }
            return accumulator;
          }, []);
        } else {
          return [];
        }
      });

      root.x0 = height / 2;
      root.y0 = 0;

      const collapse = (d: InsightNode) => {
        if (d.children) {
          d._children = d.children;
          d._children.forEach(collapse);
          d.children = null;
        }
      };

      const clickLink = (event: any, d: InsightNode) => {
        onEditAnswer(d);
      };

      // Toggle children on click.
      const rightClickNode = (event: PointerEvent, d: InsightNode) => {
        console.log(event);
        event.preventDefault();
        if (assigningNode) {
          return;
        }
        setAnchor({ element: event.currentTarget as HTMLElement, node: d, isValidNode: validateDialogNode(d.data, d.data.answer, d.parent?.data, false).numErrors === 0 });
      };

      const leftClickNode = (event: PointerEvent, d: InsightNode) => {
        if (assigningNode) {
          if (anchor && anchor.node) {
            onAssign(anchor.node, d);
            setAnchor(null);
          } else {
            console.error('No anchor');
          }
          setAssigningNode(false);
        } else {
          event.preventDefault();
          onOpen(d);
        }
      };

      // Creates a curved (diagonal) path from parent to the child nodes
      const diagonal = (s: any, d: any) => {
        // const path: string = `M ${s.y} ${s.x}
        //     C ${(s.y + d.y) / 2} ${s.x},
        //       ${(s.y + d.y) / 2} ${d.x},
        //       ${d.y} ${d.x}`;

        // Reverse direction to make text work
        return `M ${d.y} ${d.x}
            C ${(d.y + s.y) / 2} ${d.x},
              ${(d.y + s.y) / 2} ${s.x},
              ${s.y} ${s.x}`;
        // return `M ${s.y}, ${s.x + (height / 2)}
        //     C ${(s.y + d.y) / 2}, ${s.x},
        //       ${(s.y + d.y) / 2}, ${d.x},
        //       ${d.y + (width / 2)}, ${d.x}`;
      };

      const update = (source: InsightNode) => {
        const treeData: d3.HierarchyPointNode<IInsightDialog> = tree(root);
        const nodes: d3.HierarchyPointNode<IInsightDialog>[] = treeData.descendants();
        const links: d3.HierarchyPointNode<IInsightDialog>[] = treeData.descendants().slice(1);
        setNodes(nodes as d3.HierarchyPointNode<IInsightDialog>[]);
        // Normalize for fixed-depth.
        nodes.forEach((d: any) => {
          // const { width } = calculateRect(d.parent ? d.parent : d);
          let distance = height * distanceMultiplier;
          // if (distance < 180) {
          //   distance = 180;
          // }
          d.y = d.depth * distance;
          if (d.parent) {
            const index = d.parent.children.findIndex((c: any) => c.id === d.id);
            if (index > -1) {
              d.x = d.x + (10 * index)
            }
          }
        });

        // Tooltip

        // Remove previous
        // d3.select('#dialog-tree-visual').selectAll('.tooltip').remove();
        // console.log('REMOVE ALL TOOLTIP');
        var div = d3.select('#dialog-tree-visual').append('div')
          .attr('class', 'tooltip')
          .style('opacity', 0);

        // ****************** Nodes section ***************************

        // Update the nodes...
        const node = g.selectAll('g.node')
          .data(nodes, (d: d3.HierarchyPointNode<IInsightDialog>) => {
            return d.id || ((d.id as any) = ++i);
          });

        // Enter any new modes at the parent's previous position.
        const nodeEnter = node.enter().append('g')
          .attr('class', 'node')
          .attr('transform', (d: InsightNode) => 'translate(' + source.y0 + ',' + source.x0 + ')')
          .on('contextmenu', rightClickNode)
          .on('click', leftClickNode)
          .on('mouseover', (event: any, d: InsightNode) => {
            const validation: IValidationData = validateDialogNode(d.data, d.data.answer, d.parent && d.parent.data ? d.parent.data as any : null);
            div.transition()
              .duration(200)
              .style('opacity', .9);
            const unique = isUnique(d);
            let text1 = '';
            if (!unique) {
              text1 += `<p style="font-size: 10px">❗ ID: ${d.data.id} is not unique</p>`;
            }
            const text2 = Object.keys(validation.errors).reduce((accumulator: string, key: string) => {
              accumulator += `<p style="font-size: 10px">❗ ${key}: ${validation.errors[key]}</p>`
              return accumulator;
            }, text1);
            const text3 = Object.keys(validation.warnings).reduce((accumulator: string, key: string) => {
              accumulator += `<p style="font-size: 10px">⚠️ ${key}: ${validation.warnings[key]}</p>`
              return accumulator;
            }, '');
            div.html(`<div>${text2 + text3}</div>`)
              .style('left', (event.pageX) + 'px')
              .style('top', (event.pageY - 28) + 'px')
              .style('visibility', (validation.numErrors > 0 || validation.numWarnings > 0 || !unique) ? 'visible' : 'hidden');
          })
          .on('mouseout', (d: InsightNode) => {
            div.transition()
              .duration(500)
              .style('opacity', 0);
          });


        // Add Circle for the nodes
        nodeEnter.append('rect')
          .attr('class', (d: InsightNode) => {
            let classes = 'node';
            if (validateDialogNode(d.data, d.data.answer, d.parent?.data, false).numErrors > 0 || !isUnique(d)) {
              classes += ' error';
            }
            if (isCompletion(d.data.completionCriteria)) {
              classes += ' complete';
            }
            if (anchor && anchor.node && d.id === anchor.node.data.parent) {
              classes += ' selected'
            }
            return classes;
          })
          .attr('rx', 4)
          .attr('ry', 4)
          .attr('width', 1e-6)
          .attr('height', 1e-6)
          .style('fill', (d: InsightNode) => d._children ? 'lightsteelblue' : '#fff');

        // Add labels for the nodes
        nodeEnter.append('text')
          .attr('dy', '1.35em')
          .attr('id', (d: InsightNode) => `${d.id}`)
          .attr('x', (d: InsightNode) => d.children || d._children ? 0 : 0)
          // .attr("text-anchor", "middle")
          .attr('class', 'title')
          .each(function (d: InsightNode) {
            const lines: string[] = calculateLines(d);
            for (const line of lines) {
              // @ts-ignore
              d3.select(this)
                .append('tspan')
                .text(() => line)
                .attr('x', 12) // with margin
                .attr('dy', 12);
              // .attr("text-anchor", "middle");
            }
          });

        // UPDATE
        const nodeUpdate = nodeEnter.merge(node);

        // Transition to the proper position for the node
        nodeUpdate.transition()
          .duration(duration)
          .attr('transform', (d: InsightNode) => 'translate(' + d.y + ',' + d.x + ')');

        // Update the node attributes and style
        nodeUpdate.select('rect.node')
          .attr('width', (d: InsightNode) => {
            const { width } = calculateRect(d);
            return width;
          })
          .attr('height', (d: InsightNode) => {
            const { height } = calculateRect(d);
            return height;
          })
          .style('fill', (d: InsightNode) => d._children ? 'lightsteelblue' : '#fff')
          .attr('cursor', 'pointer');

        // Remove any exiting nodes
        const nodeExit = node.exit().transition()
          .duration(duration)
          .attr('transform', (d: InsightNode) => 'translate(' + source.y + ',' + source.x + ')')
          .remove();

        // On exit reduce the node size to 0
        nodeExit.select('rect')
          .attr('width', 1e-6)
          .attr('height', 1e-6);

        // On exit reduce the opacity of text labels
        nodeExit.select('text')
          .style('fill-opacity', 1e-6);

        // ****************** links section ***************************

        // Update the links...
        const link = g.selectAll('path.link')
          .data(links, (d: InsightNode) => d.id);

        // Enter any new links at the parent's previous position.
        const linkEnter = link.enter()
          .insert('g', 'g')
          .on('click', clickLink)
          .on('mouseover', (event: any, d: InsightNode) => {
            div.transition()
              .duration(200)
              .style('opacity', .9);
            div.html(d.data.answer)
              .style('left', (event.pageX) + 'px')
              .style('top', (event.pageY - 28) + 'px');
          })
          .on('mouseout', (d: InsightNode) => {
            div.transition()
              .duration(500)
              .style('opacity', 0);
          });


        const linkEnterPath = linkEnter.append('path')
          .attr('id', (d: InsightNode) => `${d.id as string}-answer`) // Unique id of the path
          .attr('class', 'link')
          .attr('d', (d: InsightNode) => {
            const o = { x: source.x0, y: source.y0 };
            return diagonal(o, o);
          });

        linkEnter
          .append('text')
          .append('textPath') //append a textPath to the text element
          .attr('xlink:href', (d: InsightNode) => `#${d.id as string}-answer`) //place the ID of the path here
          .attr('class', 'link-text')
          .style('text-anchor', 'middle') //place the text halfway on the arc
          .attr('startOffset', '50%')
          .text((d: InsightNode) => MessageTemplating.process(
            d.data.answer ? d.data.answer : '(No answer)',
            testDog,
            testProfile
          ));
        // .text((d: InsightNode) => ''); // fix the flip

        // UPDATE
        const linkUpdate = linkEnterPath.merge(link);
        // linkUpdate.append('text')

        // Transition back to the parent element position
        linkUpdate.transition()
          .duration(duration)
          .attr(
            'd',
            (d: InsightNode) => {
              const rec1: DOMRectSafe = calculateRect(d);
              if (d.parent) {
                const rec2: DOMRectSafe = calculateRect(d.parent ? d.parent : d);
                // console.log(`Test.\n\tNode: x: ${d.x}, y: ${d.y}.\n\tRec: x: ${rec1.x}, y: ${rec1.y}`);
                return diagonal(rec1, rec2);
              } else {
                console.error('no parent');
                const o = { x: source.x0, y: source.y0 };
                return diagonal(rec1, o);
              }
            }
          );

        // Remove any exiting links
        const linkExit = link.exit().transition()
          .duration(duration)
          .attr('d', (d: InsightNode) => {
            var o = { x: source.x, y: source.y };
            return diagonal(o, o);
          })
          .remove();

        // Store the old positions for transition.
        nodes.forEach((d: any) => {
          d.x0 = d.x;
          d.y0 = d.y;
        });
      };

      const zoom = d3.zoom()
        .scaleExtent([0.1, 8])
        .on('zoom', (event) => {
          svg.selectAll('g.view')
            .attr('transform', event.transform);
        });

      svg.call(zoom);

      // Initialize
      // (root.children as any).forEach(collapse);
      update(root as any);
      update(root as any);
    },
    [indicator, assigningNode]
  );

  return (
    <div id="dialog-tree-visual">
      <Snackbar
        open={assigningNode}
        onClose={() => setAssigningNode(false)}
        message="Click another node to assign to this node"
        action={
          <Button endIcon={<CloseIcon/>} size="small" color="primary" onClick={() => setAssigningNode(false)}>
            Cancel
          </Button>
        }
      />
      <svg
        ref={ref}
        style={{
          height: size.height,
          width: size.width
          // transform: `translate(${margin.left}, ${margin.top})`
        }}
      />
      <Menu
        id="simple-menu"
        anchorEl={anchor && anchor.element}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        anchorPosition={{ top: 0, left: 0 }}
        keepMounted
        open={!!anchor && !!anchor.element}
        onClose={() => setAnchor(null)}
      >
        {anchor && anchor.node.parent && <MenuItem onClick={() => handleCopy('hard')}>Copy</MenuItem>}
        {anchor && anchor.node.data.responses && Object.keys(anchor.node.data.responses).length > 0 && <MenuItem onClick={() => handleCopy('soft')}>Copy (Soft)</MenuItem>}
        <MenuItem onClick={() => handleCopy('instructions')}>Copy (Instructions)</MenuItem>
        {copyNode && anchor && !!anchor.node.parent && <MenuItem onClick={handlePaste}>Paste {copyNode.type !== 'hard' ? `(${copyNode.type})` : ''}</MenuItem>}
        {anchor && anchor.isValidNode && <MenuItem onClick={handleAdd}>Add</MenuItem>}
        {/*{anchor && anchor.isValidNode && <MenuItem onClick={handlePreAssign}>Assign</MenuItem>}*/}
        {anchor && !!anchor.node.parent && <MenuItem onClick={handleRemove}>Remove</MenuItem>}
        <MenuItem onClick={handleRequestUpdate}>Refresh</MenuItem>
        <MenuItem onClick={() => handleClear()}>Clear</MenuItem>
      </Menu>
    </div>
  );
}
